import axios from 'axios';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';

/**
 * WhatsApp inbox (GID-2697, M2 + M3 outbound), backed by Meta's Cloud API.
 *
 * The shapes below mirror gid-crud's `WhatsappConversationView` /
 * `WhatsappMessageView` rather than the `@gid/models` entities on purpose: the
 * entities live in a prerelease of `@gid/models` that gid-apps does not depend
 * on, and the API deliberately does not project every column.
 */
export interface WhatsappConversation {
  id: string;
  waPhone: string;
  /** The customer's WhatsApp profile name. Customer-controlled — never v-html it. */
  profileName: string | null;
  channelType: string;
  /** GID inbox triage state, not a Meta field. */
  status: string;
  /**
   * Whether a free-form reply is allowed right now. Computed server-side from the
   * last inbound message, because Meta never reports the window and a value
   * mirrored into a column would be stale the moment the clock moved.
   */
  timeWindowOpen: boolean;
  timeWindowExpiresAt: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  lastMessageAt: string | null;
  contactId: string | null;
  accountId: string | null;
  /** Resolved server-side from the linked contact (M5); null while unlinked. */
  contactName: string | null;
  /** The linked contact's account name; null while unlinked. */
  accountName: string | null;
  createdAt: string;
}

/** A live "who is this number?" candidate (M5) — computed, never persisted. */
export interface ContactSuggestion {
  contactId: string;
  name: string | null;
  email: string | null;
  accountId: string | null;
  phone: string | null;
  mobilePhone: string | null;
  confidence: 'exact' | 'heuristic';
}

export interface WhatsappNote {
  id: string;
  conversationId: string;
  authorContactId: string | null;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface WhatsappJobLink {
  id: string;
  conversationId: string;
  jobApiId: string;
  jobName: string | null;
  jobStatus: string | null;
  linkedBy: string | null;
  createdAt: string;
}

/** One language variant of a WhatsApp template mirror (M6). */
export interface WhatsappTemplateLang {
  locale: string;
  content: string;
  /** APPROVED | PENDING | REJECTED | … | null when never synced with Meta. */
  metaStatus: string | null;
  parameterCount: number;
}

export interface WhatsappTemplate {
  path: string;
  name: string;
  category: string | null;
  variables: unknown | null;
  langs: WhatsappTemplateLang[];
}

/** A consented contact the admin may start a conversation with (M6). */
export interface ConsentedContact {
  contactId: string;
  name: string | null;
  email: string | null;
  accountId: string | null;
  phone: string | null;
  mobilePhone: string | null;
  /** E.164 the send would target; null when no number normalises cleanly. */
  waPhone: string | null;
  consentAt: string;
}

export interface WhatsappMessage {
  id: string;
  conversationId: string;
  /** The wamid — Meta's message id. */
  waMessageId: string | null;
  direction: 'inbound' | 'outbound';
  author: string;
  authorContactId: string | null;
  contentType: string;
  body: string | null;
  media: Record<string, any> | null;
  status: string | null;
  errors: Record<string, any> | null;
  inReplyTo: string | null;
  waTimestamp: string | null;
  createdAt: string;
}

interface Page<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

type RealtimeEvent =
  | {
      type: 'whatsapp:message';
      conversation: WhatsappConversation;
      message: WhatsappMessage;
    }
  | { type: 'whatsapp:conversation'; conversation: WhatsappConversation };

const API = '/data-api/whatsapp';
const MESSAGE_PAGE_SIZE = 50;

/** Newest activity first; a conversation with no message yet sinks to the bottom. */
function byRecency(a: WhatsappConversation, b: WhatsappConversation): number {
  const at = a.lastMessageAt ? Date.parse(a.lastMessageAt) : -Infinity;
  const bt = b.lastMessageAt ? Date.parse(b.lastMessageAt) : -Infinity;
  if (at !== bt) return bt - at;
  return Date.parse(b.createdAt) - Date.parse(a.createdAt);
}

/** Thread order. Meta's timestamp is nullable; fall back like the API does. */
function messageTime(m: WhatsappMessage): number {
  return Date.parse(m.waTimestamp || m.createdAt);
}

export const moduleName = 'whatsapp';

@Module({ name: moduleName, namespaced: true })
export class WhatsappModule extends VuexModule {
  conversations: WhatsappConversation[] = [];
  conversationsTotal = 0;
  conversationsLoading = false;

  selectedId: string | null = null;
  messages: WhatsappMessage[] = [];
  messagesTotal = 0;
  messagesLoading = false;
  messagesPage = 1;
  /**
   * Bumped on every thread selection. Guarding a late response on `selectedId`
   * alone is not enough: clicking A, then B, then A issues two fetches for A,
   * and the slower one would overwrite the newer thread with its stale page.
   */
  messagesRequestId = 0;

  /** Last transport/API error, surfaced in the UI rather than only the console. */
  error: string | null = null;

  /** True while a reply is being handed to the API (not: until delivered). */
  sending = false;

  // ------------------------------------------------- details panel (M5)

  notes: WhatsappNote[] = [];
  jobLinks: WhatsappJobLink[] = [];
  suggestions: ContactSuggestion[] = [];
  suggestionsLoaded = false;
  detailsLoading = false;
  /** True while a panel write (link, note, …) is in flight. */
  detailsBusy = false;
  /**
   * The panel's own error channel, apart from `error` for the same reason as
   * `sendError`: a rejected link belongs next to the panel, not in the
   * page-level alert.
   */
  detailsError: string | null = null;

  // ---------------------------------------------------- template send (M6)

  templates: WhatsappTemplate[] = [];
  templatesStale = false;
  templatesLoading = false;
  contactResults: ConsentedContact[] = [];
  contactSearchLoading = false;
  templateSending = false;
  /** Modal-scoped error channel (consent refusals, approval refusals, …). */
  templateSendError: string | null = null;
  /**
   * The composer's own error channel, kept apart from `error`: a rejected send
   * (e.g. "the 24-hour window closed at …") belongs next to the input that
   * caused it, not in the page-level alert where it reads like a fetch failure.
   */
  sendError: string | null = null;

  get selectedConversation(): WhatsappConversation | null {
    return this.conversations.find((c) => c.id === this.selectedId) || null;
  }

  get hasOlderMessages(): boolean {
    return this.messages.length < this.messagesTotal;
  }

  @Mutation
  setError(error: string | null) {
    this.error = error;
  }

  @Mutation
  setSending(sending: boolean) {
    this.sending = sending;
  }

  @Mutation
  setSendError(error: string | null) {
    this.sendError = error;
  }

  @Mutation
  setConversationsLoading(loading: boolean) {
    this.conversationsLoading = loading;
  }

  @Mutation
  setConversations(page: Page<WhatsappConversation>) {
    this.conversations = [...page.data].sort(byRecency);
    this.conversationsTotal = page.total;
  }

  /**
   * Insert or replace, then re-sort. A live message bumps its conversation to
   * the top of the inbox; an update to one already on screen must replace it
   * rather than duplicate it.
   *
   * `conversationsTotal` is deliberately left alone: an event can be for a
   * conversation that exists server-side but fell outside the fetched window,
   * and counting it here would inflate the server's total. It only feeds the
   * "showing N of total" hint, which the next fetch corrects.
   */
  @Mutation
  upsertConversation(conversation: WhatsappConversation) {
    const others = this.conversations.filter((c) => c.id !== conversation.id);
    this.conversations = [conversation, ...others].sort(byRecency);
  }

  @Mutation
  setSelectedId(id: string | null) {
    this.selectedId = id;
    this.messages = [];
    this.messagesTotal = 0;
    this.messagesPage = 1;
    this.messagesRequestId += 1;
    // A rejection explains the conversation it happened on, not the next one.
    this.sendError = null;
    // Panel data is per-conversation; showing the previous thread's notes for
    // even a beat would misattribute them.
    this.notes = [];
    this.jobLinks = [];
    this.suggestions = [];
    this.suggestionsLoaded = false;
    this.detailsError = null;
  }

  @Mutation
  setDetails(payload: { notes: WhatsappNote[]; jobLinks: WhatsappJobLink[] }) {
    this.notes = payload.notes;
    this.jobLinks = payload.jobLinks;
  }

  @Mutation
  setDetailsLoading(loading: boolean) {
    this.detailsLoading = loading;
  }

  @Mutation
  setDetailsBusy(busy: boolean) {
    this.detailsBusy = busy;
  }

  @Mutation
  setDetailsError(error: string | null) {
    this.detailsError = error;
  }

  @Mutation
  appendNote(note: WhatsappNote) {
    this.notes = [...this.notes, note];
  }

  @Mutation
  upsertJobLink(link: WhatsappJobLink) {
    const others = this.jobLinks.filter((l) => l.id !== link.id);
    this.jobLinks = [...others, link];
  }

  @Mutation
  removeJobLinkRow(jobApiId: string) {
    this.jobLinks = this.jobLinks.filter((l) => l.jobApiId !== jobApiId);
  }

  @Mutation
  setSuggestions(suggestions: ContactSuggestion[]) {
    this.suggestions = suggestions;
    this.suggestionsLoaded = true;
  }

  @Mutation
  setTemplates(result: {
    templates: WhatsappTemplate[];
    staleStatus: boolean;
  }) {
    this.templates = result.templates;
    this.templatesStale = result.staleStatus;
  }

  @Mutation
  setTemplatesLoading(loading: boolean) {
    this.templatesLoading = loading;
  }

  @Mutation
  setContactResults(contacts: ConsentedContact[]) {
    this.contactResults = contacts;
  }

  @Mutation
  setContactSearchLoading(loading: boolean) {
    this.contactSearchLoading = loading;
  }

  @Mutation
  setTemplateSending(sending: boolean) {
    this.templateSending = sending;
  }

  @Mutation
  setTemplateSendError(error: string | null) {
    this.templateSendError = error;
  }

  @Mutation
  setMessagesLoading(loading: boolean) {
    this.messagesLoading = loading;
  }

  @Mutation
  setMessages(page: Page<WhatsappMessage>) {
    this.messages = page.data;
    this.messagesTotal = page.total;
    this.messagesPage = page.page;
  }

  /** Older page: the API returns it oldest-first, so it goes in front. */
  @Mutation
  prependMessages(page: Page<WhatsappMessage>) {
    const known = new Set(this.messages.map((m) => m.id));
    const older = page.data.filter((m) => !known.has(m.id));
    this.messages = [...older, ...this.messages];
    this.messagesTotal = page.total;
    this.messagesPage = page.page;
  }

  /**
   * A realtime frame can be a brand-new message OR a status change to one
   * already rendered (`message_failed` re-emits the same row). Keying on id and
   * replacing in place is what keeps a failed delivery from appearing twice.
   */
  @Mutation
  upsertMessage(message: WhatsappMessage) {
    const index = this.messages.findIndex((m) => m.id === message.id);
    if (index !== -1) {
      this.messages.splice(index, 1, message);
      return;
    }
    this.messages = [...this.messages, message].sort(
      (a, b) => messageTime(a) - messageTime(b),
    );
    this.messagesTotal += 1;
  }

  @Action
  async fetchConversations() {
    this.context.commit('setConversationsLoading', true);
    try {
      const { data } = await axios.get(`${API}/conversations`, {
        params: { page: 1, pageSize: 100 },
      });
      this.context.commit('setConversations', data);
      this.context.commit('setError', null);
    } catch (error) {
      this.context.commit('setError', errorMessage(error));
    } finally {
      this.context.commit('setConversationsLoading', false);
    }
  }

  @Action
  async selectConversation(id: string | null) {
    this.context.commit('setSelectedId', id);
    if (!id) return;

    const request = this.messagesRequestId;
    this.context.commit('setMessagesLoading', true);
    try {
      const { data } = await axios.get(`${API}/conversations/${id}/messages`, {
        params: { page: 1, pageSize: MESSAGE_PAGE_SIZE },
      });
      // Drop a response that a later selection has already superseded — even
      // one for this same conversation.
      if (this.messagesRequestId === request) {
        this.context.commit('setMessages', data);
      }
      this.context.commit('setError', null);
    } catch (error) {
      this.context.commit('setError', errorMessage(error));
    } finally {
      if (this.messagesRequestId === request) {
        this.context.commit('setMessagesLoading', false);
      }
    }
  }

  @Action
  async loadOlderMessages() {
    const id = this.selectedId;
    if (!id || this.messagesLoading || !this.hasOlderMessages) return;

    const request = this.messagesRequestId;
    this.context.commit('setMessagesLoading', true);
    try {
      const { data } = await axios.get(`${API}/conversations/${id}/messages`, {
        params: { page: this.messagesPage + 1, pageSize: MESSAGE_PAGE_SIZE },
      });
      if (this.messagesRequestId === request) {
        this.context.commit('prependMessages', data);
      }
    } catch (error) {
      this.context.commit('setError', errorMessage(error));
    } finally {
      if (this.messagesRequestId === request) {
        this.context.commit('setMessagesLoading', false);
      }
    }
  }

  /**
   * Queue one free-form reply. Resolves `true` when the API accepted it (202) —
   * accepted, not delivered: the message appears in the thread via the
   * WebSocket once gid-crud records it, including as a failed bubble if the
   * send dies later. A rejection (window closed, sending disabled here, …)
   * lands in `sendError` with the API's human-readable reason.
   */
  @Action
  async sendMessage(payload: {
    conversationId: string;
    text: string;
  }): Promise<boolean> {
    this.context.commit('setSending', true);
    try {
      await axios.post(
        `${API}/conversations/${payload.conversationId}/messages`,
        { text: payload.text },
      );
      this.context.commit('setSendError', null);
      return true;
    } catch (error) {
      this.context.commit('setSendError', errorMessage(error));
      return false;
    } finally {
      this.context.commit('setSending', false);
    }
  }

  @Action
  applyRealtimeEvent(event: RealtimeEvent) {
    this.context.commit('upsertConversation', event.conversation);
    if (
      event.type === 'whatsapp:message' &&
      event.conversation.id === this.selectedId
    ) {
      this.context.commit('upsertMessage', event.message);
    }
  }

  // ------------------------------------------------- details panel (M5)

  @Action
  async fetchDetails(conversationId: string) {
    this.context.commit('setDetailsLoading', true);
    try {
      const [notes, jobLinks] = await Promise.all([
        axios.get(`${API}/conversations/${conversationId}/notes`),
        axios.get(`${API}/conversations/${conversationId}/jobs`),
      ]);
      // A slow response for a conversation the admin already left is dropped.
      if (this.selectedId === conversationId) {
        this.context.commit('setDetails', {
          notes: notes.data,
          jobLinks: jobLinks.data,
        });
      }
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
    } finally {
      this.context.commit('setDetailsLoading', false);
    }
  }

  @Action
  async addNote(payload: { conversationId: string; body: string }) {
    this.context.commit('setDetailsBusy', true);
    try {
      const { data } = await axios.post(
        `${API}/conversations/${payload.conversationId}/notes`,
        { body: payload.body },
      );
      if (this.selectedId === payload.conversationId) {
        this.context.commit('appendNote', data);
      }
      this.context.commit('setDetailsError', null);
      return true;
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
      return false;
    } finally {
      this.context.commit('setDetailsBusy', false);
    }
  }

  @Action
  async addJobLink(payload: { conversationId: string; jobRef: string }) {
    // An order number is what the admin usually has on screen; anything that
    // looks like one travels as jobName, everything else as the api id.
    const ref = payload.jobRef.trim();
    const body = /^O-?\d+$/i.test(ref) ? { jobName: ref } : { jobApiId: ref };

    this.context.commit('setDetailsBusy', true);
    try {
      const { data } = await axios.post(
        `${API}/conversations/${payload.conversationId}/jobs`,
        body,
      );
      if (this.selectedId === payload.conversationId) {
        this.context.commit('upsertJobLink', data);
      }
      this.context.commit('setDetailsError', null);
      return true;
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
      return false;
    } finally {
      this.context.commit('setDetailsBusy', false);
    }
  }

  @Action
  async removeJobLink(payload: { conversationId: string; jobApiId: string }) {
    this.context.commit('setDetailsBusy', true);
    try {
      await axios.delete(
        `${API}/conversations/${
          payload.conversationId
        }/jobs/${encodeURIComponent(payload.jobApiId)}`,
      );
      if (this.selectedId === payload.conversationId) {
        this.context.commit('removeJobLinkRow', payload.jobApiId);
      }
      this.context.commit('setDetailsError', null);
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
    } finally {
      this.context.commit('setDetailsBusy', false);
    }
  }

  @Action
  async fetchSuggestions(conversationId: string) {
    this.context.commit('setDetailsBusy', true);
    try {
      const { data } = await axios.get(
        `${API}/conversations/${conversationId}/contact-suggestions`,
      );
      if (this.selectedId === conversationId) {
        this.context.commit('setSuggestions', data);
      }
      this.context.commit('setDetailsError', null);
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
    } finally {
      this.context.commit('setDetailsBusy', false);
    }
  }

  @Action
  async linkContact(payload: { conversationId: string; contactId: string }) {
    this.context.commit('setDetailsBusy', true);
    try {
      const { data } = await axios.post(
        `${API}/conversations/${payload.conversationId}/contact-link`,
        { contactId: payload.contactId },
      );
      // The API answers with the refreshed conversation view.
      this.context.commit('upsertConversation', data);
      this.context.commit('setDetailsError', null);
      return true;
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
      return false;
    } finally {
      this.context.commit('setDetailsBusy', false);
    }
  }

  @Action
  async unlinkContact(conversationId: string) {
    this.context.commit('setDetailsBusy', true);
    try {
      const { data } = await axios.delete(
        `${API}/conversations/${conversationId}/contact-link`,
      );
      this.context.commit('upsertConversation', data);
      this.context.commit('setDetailsError', null);
    } catch (error) {
      this.context.commit('setDetailsError', errorMessage(error));
    } finally {
      this.context.commit('setDetailsBusy', false);
    }
  }

  /** Template mirrors with Meta approval status (M6); refreshes the picker. */
  @Action
  async fetchTemplates() {
    this.context.commit('setTemplatesLoading', true);
    try {
      const { data } = await axios.get(`${API}/templates`);
      this.context.commit('setTemplates', data);
    } catch (error) {
      this.context.commit('setTemplateSendError', errorMessage(error));
    } finally {
      this.context.commit('setTemplatesLoading', false);
    }
  }

  /**
   * Consented contacts only — the server never returns anyone who has not
   * opted in, which is the block the opt-in exists to enforce. POST because
   * the query is a name/phone fragment (PII stays out of request logs).
   */
  @Action
  async searchContacts(query: string) {
    this.context.commit('setContactSearchLoading', true);
    try {
      const { data } = await axios.post(`${API}/contact-search`, { query });
      this.context.commit('setContactResults', data);
      this.context.commit('setTemplateSendError', null);
    } catch (error) {
      this.context.commit('setTemplateSendError', errorMessage(error));
    } finally {
      this.context.commit('setContactSearchLoading', false);
    }
  }

  /**
   * The same consent-gated projection, for contacts we already know by id
   * (GID-2711): the admin partner view holds the partner's contacts and only
   * needs to learn which of them may be messaged. Contacts without consent are
   * simply absent from the result — an empty list means "nobody here opted in".
   */
  @Action
  async lookupContacts(contactIds: string[]) {
    if (!contactIds.length) {
      this.context.commit('setContactResults', []);
      return;
    }
    this.context.commit('setContactSearchLoading', true);
    try {
      const { data } = await axios.post(`${API}/contact-search`, {
        contactIds,
      });
      this.context.commit('setContactResults', data);
      this.context.commit('setTemplateSendError', null);
    } catch (error) {
      this.context.commit('setTemplateSendError', errorMessage(error));
    } finally {
      this.context.commit('setContactSearchLoading', false);
    }
  }

  /**
   * Business-initiated template message (M6). Resolves to the conversation id
   * on acceptance (queued, not delivered — the thread shows the outcome), or
   * null with the server's reason in `templateSendError`.
   */
  @Action
  async sendTemplateMessage(payload: {
    contactId: string;
    templatePath: string;
    locale: string;
    parameters: string[];
    jobName?: string;
    /** Link the message to these jobs — puts it in their activity feeds. */
    jobApiIds?: string[];
  }): Promise<string | null> {
    this.context.commit('setTemplateSending', true);
    try {
      const { data } = await axios.post(`${API}/template-messages`, payload);
      this.context.commit('setTemplateSendError', null);
      return data.conversationId;
    } catch (error) {
      this.context.commit('setTemplateSendError', errorMessage(error));
      return null;
    } finally {
      this.context.commit('setTemplateSending', false);
    }
  }

  /**
   * A short-lived signed URL for a stored attachment (M5). No store state —
   * each bubble caches its own URL; the signature expires server-side anyway.
   * Resolves null while the fetch is pending or after it failed; the bubble
   * shows the matching placeholder.
   */
  @Action
  async fetchMediaUrl(payload: {
    conversationId: string;
    messageId: string;
  }): Promise<{ url: string; mimeType: string | null } | null> {
    try {
      const { data } = await axios.get(
        `${API}/conversations/${payload.conversationId}/messages/${payload.messageId}/media`,
      );
      return data;
    } catch (error) {
      return null;
    }
  }
}

function errorMessage(error: any): string {
  return error?.response?.data?.message || error?.message || 'Unexpected error';
}

export default WhatsappModule;
